/* famous-map.js (pacote E2) — o mapa da vista "Map" do hub Famous Paintings.

   Contrato: `snippets/famous-view-map.liquid` imprime

     <div class="famous-map" data-famous-map hidden>
       <div class="famous-map__canvas" data-famous-map-canvas role="region" aria-label="…"></div>
       <p class="famous-map__note" data-famous-map-note>…</p>
       <script type="application/json" data-famous-map-data>[ … uma entrada por obra … ]</script>
     </div>

   e cada entrada tem rank, title, artist, museum, museum_url, city, country, lat, lng, anchor.
   O `artist` chega e é guardado, mas NÃO aparece no popup (ver o comentário em `popupHtml`).

   Regras que este arquivo cumpre:
   - Leaflet 1.9.4 do cdnjs com SRI. Os dois hashes abaixo NÃO foram chutados: vieram de
     https://api.cdnjs.com/libraries/leaflet/1.9.4?fields=sri em 15/09/2026.
   - O Leaflet só é pedido quando a caixa do mapa entra em tela (IntersectionObserver).
     Antes disso: zero requisição de tile, zero requisição ao cdnjs.
   - A caixa nasce com `hidden`. Ela é revelada no boot, ANTES de qualquer requisição, e só se
     houver ao menos uma coordenada numérica. Sem coordenada, com lista vazia ou com o cdnjs
     fora do ar, ela volta para `hidden`: não sobra buraco no layout nem erro no console.
   - Um pino por museu, agrupando as obras daquele museu.
   - Atribuição do OpenStreetMap sempre visível.
   - prefers-reduced-motion desliga zoom, fade e animação de marcador; nada de voo de câmera. */

(function () {
  'use strict';

  var CDN = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/';
  var CSS_URL = CDN + 'leaflet.min.css';
  var CSS_SRI = 'sha512-h9FcoyWjHcOcmEVkxOfTLnmZFWIH0iZhZT1H2TbOq55xssQGEJHEaIm+PgoUaZbRvQTNTluNOEfb1ZRy6D3BOw==';
  var JS_URL = CDN + 'leaflet.min.js';
  var JS_SRI = 'sha512-puJW3E/qXDqYp9IfhAI54BJEaWIfloJ7JWs7OeD5i6ruC9JZL1gERT1wjtwXFlh7CjE7ZJ+/vcRZRkIYIb6p4g==';

  var TILES = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
  var ATTRIB =
    '&copy; <a href="https://www.openstreetmap.org/copyright" rel="noopener">OpenStreetMap</a> contributors';

  var pending = null;

  function loadLeaflet() {
    if (window.L && window.L.map) return Promise.resolve(window.L);
    if (pending) return pending;
    pending = new Promise(function (resolve, reject) {
      var link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = CSS_URL;
      link.integrity = CSS_SRI;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);

      var s = document.createElement('script');
      s.src = JS_URL;
      s.integrity = JS_SRI;
      s.crossOrigin = 'anonymous';
      s.async = true;
      s.onload = function () {
        if (window.L && window.L.map) resolve(window.L);
        else reject(new Error('leaflet loaded without L'));
      };
      s.onerror = function () {
        reject(new Error('leaflet blocked or offline'));
      };
      document.head.appendChild(s);
    });
    return pending;
  }

  function esc(v) {
    return String(v == null ? '' : v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* só http(s). Nada de javascript: vindo do dado. */
  function safeUrl(u) {
    if (!u) return '';
    var t = String(u).trim();
    return /^https?:\/\//i.test(t) ? t : '';
  }

  function num(v) {
    var n = typeof v === 'number' ? v : parseFloat(v);
    return isFinite(n) ? n : null;
  }

  /* uma entrada por obra vira um grupo por museu.
     A chave é museu + cidade; obra sem museu cai num grupo da própria coordenada. */
  function group(rows) {
    var order = [];
    var byKey = {};
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i] || {};
      var lat = num(r.lat);
      var lng = num(r.lng);
      if (lat === null || lng === null) continue;
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) continue;

      var museum = (r.museum || '').trim();
      var city = (r.city || '').trim();
      var key = museum
        ? museum.toLowerCase() + '@' + city.toLowerCase()
        : '@@' + lat.toFixed(4) + ',' + lng.toFixed(4);

      var g = byKey[key];
      if (!g) {
        g = byKey[key] = {
          museum: museum,
          city: city,
          country: (r.country || '').trim(),
          url: safeUrl(r.museum_url),
          lat: lat,
          lng: lng,
          works: []
        };
        order.push(g);
      }
      if (!g.url) g.url = safeUrl(r.museum_url);
      g.works.push({
        rank: r.rank,
        title: r.title || '',
        artist: r.artist || '',
        anchor: (r.anchor || '').replace(/^#/, '')
      });
    }
    /* museu com mais obras primeiro: o popup mais rico fica por cima quando dois pinos
       caem quase no mesmo ponto. */
    order.sort(function (a, b) {
      return b.works.length - a.works.length;
    });
    return order;
  }

  function popupHtml(g) {
    var out = '';
    var label = g.museum || g.city || 'Museum';
    out += '<p class="famous-map__museum">';
    out += g.url
      ? '<a href="' + esc(g.url) + '" rel="noopener">' + esc(label) + '</a>'
      : esc(label);
    out += '</p>';

    var place = [g.city, g.country]
      .filter(function (x) {
        return x;
      })
      .join(', ');
    if (place) out += '<p class="famous-map__place">' + esc(place) + '</p>';

    /* Uma linha por obra: número do ranking + título. O artista sai daqui de propósito — num
       popup de 280px ele empurrava o título para duas linhas e a lista do Louvre (13 obras)
       virava um paredão. O artista está na ficha, que é para onde a âncora leva. */
    if (g.works.length) {
      out += '<ul class="famous-map__works">';
      for (var i = 0; i < g.works.length; i++) {
        var w = g.works[i];
        var text = esc(w.title);
        out += '<li>';
        out += '<span class="famous-map__rank" aria-hidden="true">' + esc(w.rank) + '</span>';
        out += w.anchor
          ? '<a href="#' + esc(w.anchor) + '">' + text + '</a>'
          : '<span class="famous-map__title">' + text + '</span>';
        out += '</li>';
      }
      out += '</ul>';
    }
    return out;
  }

  function still() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function draw(box, groups) {
    var canvas = box.querySelector('[data-famous-map-canvas]');
    if (!canvas || box.dataset.famousMapReady) return;

    loadLeaflet()
      .then(function (L) {
        if (box.dataset.famousMapReady) return;
        box.dataset.famousMapReady = '1';

        var quiet = still();
        var map = L.map(canvas, {
          scrollWheelZoom: false, /* a roda rola a página, não dá zoom: o mapa não sequestra o scroll */
          zoomAnimation: !quiet,
          fadeAnimation: !quiet,
          markerZoomAnimation: !quiet,
          attributionControl: true
        });

        L.tileLayer(TILES, { maxZoom: 18, attribution: ATTRIB }).addTo(map);

        var icon = L.divIcon({
          className: 'famous-map__marker',
          html: '<span class="famous-map__pin"></span>',
          iconSize: [24, 24],
          iconAnchor: [12, 12],
          popupAnchor: [0, -12]
        });

        /* Largura do popup pela largura do mapa, e autoPan com folga para os controles.
           Em 390px o popup de 280 ficava com 322 de largura num mapa de 358 e passava por baixo
           dos botões de zoom, cortando o nome do museu (medido). O Leaflet desenha os controles
           SEMPRE acima do painel dos popups, então quem tem de sair da frente é o popup. */
        var cw = canvas.clientWidth || 320;
        var popMax = Math.max(180, Math.min(280, cw - 110));
        var popOpts = {
          maxWidth: popMax,
          minWidth: Math.min(200, popMax),
          autoPan: true,
          autoPanPaddingTopLeft: [46, 12],
          autoPanPaddingBottomRight: [12, 26]
        };

        var pts = [];
        var marks = [];
        for (var i = 0; i < groups.length; i++) {
          var g = groups[i];
          var label = g.museum || g.city;
          var n = g.works.length;
          var name =
            label + ' — ' + n + (n === 1 ? ' painting from this list' : ' paintings from this list');
          var mk = L.marker([g.lat, g.lng], {
            icon: icon,
            title: name,
            alt: name,
            keyboard: true,
            riseOnHover: true
          }).addTo(map);
          mk.bindPopup(popupHtml(g), popOpts);
          marks.push([mk, name]);
          pts.push([g.lat, g.lng]);
        }

        map.fitBounds(L.latLngBounds(pts).pad(0.15), { animate: false });
        if (map.getZoom() > 6) map.setZoom(6);

        /* SÓ AQUI o pino tem elemento. `addLayer` numa mapa ainda sem vista adia o `_initIcon`
           via `whenReady`, e é o `fitBounds` acima que dá a vista. Antes dele, `getElement()`
           devolve null — foi o que aconteceu na primeira medição.
           O Leaflet põe role="button" e tabindex="0" no pino, mas o nome vem do `alt`, que num
           <div> não diz nada ao leitor de tela. aria-label resolve. */
        for (var k = 0; k < marks.length; k++) {
          var el = marks[k][0].getElement();
          if (el) el.setAttribute('aria-label', marks[k][1]);
        }

        /* --------------------------------------------------------------- teclado no popup

           O Leaflet abre o popup mas deixa o foco no pino, e o painel do popup fica DEPOIS do
           painel dos pinos no DOM: quem abrisse o Louvre com Enter precisava de 43 Tabs para
           chegar nos links do próprio popup (medido). Por isso o foco vai para o conteúdo.

           ARMADILHA que isso cria: o Leaflet só escuta Escape enquanto o CONTÊINER do mapa tem
           foco — ele liga o `keydown` no `document` no evento `focus` do mapa e desliga no
           `blur`. Tirar o foco do contêiner desligava o Escape (medido: o popup não fechava).
           Daí o handler próprio abaixo. */
        var focusFn = function (el) {
          try {
            el.focus({ preventScroll: true });
          } catch (err) {
            el.focus();
          }
        };
        var aberto = null;

        map.on('popupopen', function (e) {
          var pel = e.popup.getElement();
          if (!pel) return;
          var content = pel.querySelector('.leaflet-popup-content');
          if (!content) return;
          content.setAttribute('tabindex', '-1');
          content.setAttribute('role', 'dialog');
          var head = content.querySelector('.famous-map__museum');
          content.setAttribute('aria-label', head ? head.textContent : 'Museum');
          focusFn(content);
        });

        for (var m = 0; m < marks.length; m++) {
          marks[m][0].on('popupopen', function (e) {
            aberto = e.target;
          });
          marks[m][0].on('popupclose', function (e) {
            if (aberto === e.target) aberto = null;
            /* devolve o foco ao pino em dois casos: o foco ainda está DENTRO do popup que está
               saindo (fechar no × pelo teclado — com fadeAnimation o Leaflet só tira o nó do DOM
               200ms depois, então aqui ele ainda existe e ainda está focado), ou o foco já se
               perdeu para o body. Se a pessoa clicou no mapa ou já está em outro lugar da
               página, o foco dela não é mexido. */
            var a = document.activeElement;
            var pel = e.popup && e.popup.getElement ? e.popup.getElement() : null;
            var dentroDoPopup = !!(a && pel && pel.contains(a));
            var perdido = !a || a === document.body || a === document.documentElement;
            if (!dentroDoPopup && !perdido) return;
            var mel = e.target.getElement();
            if (mel) focusFn(mel);
          });
        }

        box.addEventListener('keydown', function (ev) {
          if (ev.key !== 'Escape' && ev.key !== 'Esc' && ev.keyCode !== 27) return;
          if (!aberto) return;
          var mel = aberto.getElement();
          map.closePopup();
          if (mel) focusFn(mel);
          ev.stopPropagation();
        });

        var note = box.querySelector('[data-famous-map-note]');
        if (note) note.hidden = true;

        /* o número real de pinos só existe depois do agrupamento: o rótulo é corrigido aqui */
        canvas.setAttribute(
          'aria-label',
          'Map of the ' + groups.length + ' museums that hold these paintings'
        );

        /* a vista Mapa pode estar escondida quando o Leaflet desenha (o E1 troca de vista por
           classe). Quando ela aparece, o Leaflet precisa remedir o contêiner ou os tiles ficam
           cortados. */
        if ('ResizeObserver' in window) {
          var ro = new ResizeObserver(function () {
            map.invalidateSize({ animate: false });
          });
          ro.observe(canvas);
        }
        window.addEventListener('resize', function () {
          map.invalidateSize({ animate: false });
        });
      })
      .catch(function () {
        /* cdnjs bloqueado, offline ou SRI recusado: some sem deixar buraco e sem gritar */
        box.hidden = true;
      });
  }

  function readRows(box) {
    var el = box.querySelector('[data-famous-map-data]');
    if (!el) return [];
    var rows;
    try {
      rows = JSON.parse(el.textContent || '[]');
    } catch (e) {
      return [];
    }
    if (!Array.isArray(rows)) return [];
    return rows;
  }

  function boot() {
    var boxes = document.querySelectorAll('[data-famous-map]');
    if (!boxes.length) return;

    Array.prototype.forEach.call(boxes, function (box) {
      var groups = group(readRows(box));
      if (!groups.length) return; /* fica hidden: sem coordenada, sem caixa */

      /* revelar ANTES de pedir o Leaflet. A altura é fixa no CSS, então a caixa já nasce do
         tamanho final e o mapa entra sem deslocar nada. */
      box.hidden = false;

      if (!('IntersectionObserver' in window)) {
        draw(box, groups);
        return;
      }
      var io = new IntersectionObserver(
        function (entries) {
          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;
            io.disconnect();
            draw(box, groups);
          }
        },
        { rootMargin: '200px 0px' }
      );
      io.observe(box);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
